<script lang="ts">
import type { RadioGroupRootEmits, RadioGroupRootProps } from 'reka-ui';
import type { AcceptableValue } from '#ui/types/utils';
import type { TileGroupTheme, TileGroupVariants } from './tile-group';
import { tileGroupTheme } from './tile-group';

export interface TileRadioGroupItem<TValue extends AcceptableValue = AcceptableValue> {
	label?: string;
	description?: string;
	value?: TValue;
	disabled?: boolean;
	icon?: string;
}

type TileRadioGroupKey<T extends Record<string, any>> = Extract<keyof T, string>;
type TileRadioGroupDefaultKey<T extends Record<string, any>> = 'value' extends TileRadioGroupKey<T> ? 'value' : TileRadioGroupKey<T>;
type TileRadioGroupValue<T extends Record<string, any>, K extends TileRadioGroupKey<T>> = Exclude<Extract<T[K], AcceptableValue>, undefined>;
type TileRadioGroupRootProps<T extends Record<string, any>, K extends TileRadioGroupKey<T>> =
	Pick<RadioGroupRootProps, 'as' | 'disabled' | 'loop' | 'name' | 'required'> & {
		defaultValue?: TileRadioGroupValue<T, K> | null;
		modelValue?: TileRadioGroupValue<T, K> | null;
	};

export interface TileRadioGroupProps<
	T extends Record<string, any> = TileRadioGroupItem,
	K extends TileRadioGroupKey<T> = TileRadioGroupDefaultKey<T>,
> extends TileRadioGroupRootProps<T, K> {
	/**
	 * The element or component this component should render as.
	 * @defaultValue 'div'
	 */
	as?: any;

	items: T[];

	size?: TileGroupVariants['size'];

	labelKey?: string;

	descriptionKey?: string;

	valueKey?: K;

	maxItems?: number;

	class?: string;
	ui?: TileGroupTheme;

	readonly?: boolean;
}

export type TileRadioGroupEmits<
	T extends Record<string, any> = TileRadioGroupItem,
	K extends TileRadioGroupKey<T> = TileRadioGroupDefaultKey<T>,
> = Omit<RadioGroupRootEmits, 'update:modelValue'> & {
	'update:modelValue': [payload: TileRadioGroupValue<T, K>];
	'change': [payload: Event];
};
</script>

<script setup lang="ts" generic="T extends Record<string, any>, K extends TileRadioGroupKey<T> = TileRadioGroupDefaultKey<T>">
import { reactivePick } from '@vueuse/core';
import { RadioGroupItem, RadioGroupRoot, useForwardPropsEmits } from 'reka-ui';
import { computed, ref, useId } from 'vue';
import { useFormField } from '#imports';
import { get } from '#layers/base/public';
import { useReadonly } from '#layers/ui/composables/use-readonly';

const props = withDefaults(defineProps<TileRadioGroupProps<T, K>>(), {
	labelKey: 'label',
	descriptionKey: 'description',
});
const emits = defineEmits<TileRadioGroupEmits<T, K>>();

const rootProps = useForwardPropsEmits(reactivePick(props, 'as', 'modelValue', 'defaultValue', 'loop', 'required'), emits);
const { preventKeyboardNavigation } = useReadonly(() => props.readonly);

const { emitFormChange, emitFormInput, name, size, id: _id, disabled, ariaAttrs } = useFormField<TileRadioGroupProps<T, K>>(props, { bind: false });
const id = _id.value ?? useId();

const someItemsHaveIcon = computed(() => props.items.some((item) => !!item.icon));
const valueKey = computed(() => (props.valueKey ?? 'value') as K);

const normalizedItems = computed(() => props.items ? props.items.map((item) => normalizeItem(item)) : []);
const expanded = ref(false);
const normalizedMaxItems = computed<number>(() => {
	const maxItems = props.maxItems;
	return typeof maxItems === 'number' && Number.isInteger(maxItems) && maxItems > 0 ? maxItems : Infinity;
});
const hiddenItems = computed(() => normalizedItems.value.slice(normalizedMaxItems.value));
const hasOverflowItems = computed(() =>
	normalizedItems.value.length > normalizedMaxItems.value,
);
const hasSelectedHiddenItem = computed(() => {
	const selectedValue = props.modelValue !== undefined ? props.modelValue : props.defaultValue;
	return hiddenItems.value.some((item) => Object.is(item.value, selectedValue));
});
const overflowAction = computed(() => hasOverflowItems.value && !hasSelectedHiddenItem.value ? (expanded.value ? 'show_less' : 'show_more') : null);
const visibleItems = computed(() => {
	if (expanded.value || hasSelectedHiddenItem.value) return normalizedItems.value;

	return normalizedItems.value.slice(0, normalizedMaxItems.value);
});

function normalizeItem(item: T): TileRadioGroupItem<TileRadioGroupValue<T, K>> & T {
	return {
		...item,
		value: get(item, valueKey.value) as TileRadioGroupValue<T, K>,
		label: get(item, props.labelKey),
		description: get(item, props.descriptionKey),
	};
}

function onUpdate(value: any) {
	// @ts-expect-error - 'target' does not exist in type 'EventInit'
	const event = new Event('change', { target: { value } });
	emits('change', event);
	emitFormChange();
	emitFormInput();
}

function toggleOverflowItems() {
	expanded.value = !expanded.value;
}

const resolvedTheme = computed(() => tileGroupTheme({
	icons: someItemsHaveIcon.value,
	disabled: disabled.value,
	size: size.value as TileRadioGroupProps['size'],
}));

const appendSlotProps = computed(() => ({
	allItemsVisible: visibleItems.value.length === normalizedItems.value.length,
	ui: {
		item: resolvedTheme.value.item({ class: props.ui?.item }),
		itemTextWrapper: resolvedTheme.value.itemTextWrapper({ class: props.ui?.itemTextWrapper }),
		itemLabel: resolvedTheme.value.itemLabel({ class: props.ui?.itemLabel }),
		itemDescription: resolvedTheme.value.itemDescription({ class: props.ui?.itemDescription }),
	},
}));
</script>

<template>
	<RadioGroupRoot
		:id
		:aria-label="$t('tile_radio_group.select_option')"
		v-bind="rootProps"
		:name
		:class="resolvedTheme.root({ class: [props.class, props.ui?.root, !props.readonly ? 'pointer-events-auto' : 'pointer-events-none'] })"
		:disabled
		@keydown.capture="preventKeyboardNavigation"
		@update:model-value="onUpdate"
	>
		<template
			v-for="(item, index) in visibleItems"
			:key="item.value"
		>
			<RadioGroupItem
				:value="item.value"
				:disabled="item.disabled"
				:class="resolvedTheme.item({ class: props.ui?.item })"
				:aria-label="item.label"
				v-bind="ariaAttrs"
			>
				<div v-if="$slots['item-icon'] || someItemsHaveIcon" :class="resolvedTheme.itemIcon({ class: props.ui?.itemIcon })">
					<slot v-if="$slots['item-icon']" name="item-icon" v-bind="{ item, index }">
						<DIcon v-if="item.icon" :name="item.icon" :size="resolvedTheme.itemIconSize({ class: props.ui?.itemIconSize })" />
					</slot>
					<DIcon v-else-if="item.icon" :name="item.icon" :size="resolvedTheme.itemIconSize({ class: props.ui?.itemIconSize })" />
				</div>
				<div :class="resolvedTheme.itemTextWrapper({ class: props.ui?.itemTextWrapper })">
					<p v-if="item.label || !!$slots['item-label']" :class="resolvedTheme.itemLabel({ class: props.ui?.itemLabel })">
						<slot name="item-label" v-bind="{ item, index }">
							{{ item.label }}
						</slot>
					</p>
					<p v-if="item.description || !!$slots['item-description']" :class="resolvedTheme.itemDescription({ class: props.ui?.itemDescription })">
						<slot name="item-description" v-bind="{ item, index }">
							{{ item.description }}
						</slot>
					</p>
				</div>
			</RadioGroupItem>
		</template>
		<slot name="append" v-bind="appendSlotProps" />
		<button
			v-if="overflowAction"
			type="button"
			:class="resolvedTheme.item({ class: [props.ui?.item, props.ui?.overflowItem] })"
			:disabled
			@click="toggleOverflowItems"
		>
			<slot
				name="overflow"
				v-bind="{
					action: overflowAction,
					expanded,
					hiddenItems,
					toggle: toggleOverflowItems,
					visibleItems,
				}"
			>
				<div :class="resolvedTheme.itemIcon({ class: props.ui?.itemIcon })">
					<DIcon name="lucide:ellipsis" :size="resolvedTheme.itemIconSize({ class: props.ui?.itemIconSize })" />
				</div>
				<span :class="resolvedTheme.itemLabel({ class: [props.ui?.itemLabel, 'text-center'] })">
					{{ $t(`common.${overflowAction}`) }}
				</span>
			</slot>
		</button>
	</RadioGroupRoot>
</template>
